/**
 * /app · Overview landing page — 1:1 port of mini-app.html (3-screen design
 * composite). The three design screens are rendered as a single scrollable
 * page in the Mini App shell:
 *
 *  • Screen 1 – Home: search bar · invite strip · promo bear · 6-step process
 *              · 2×2 hot-sale grid (real API)
 *  • Screen 2 – Orders preview: recent orders (real data, max 3) with status
 *              badges + navigate-to-orders CTA
 *  • Screen 3 – Warehouse preview: real user stat counters + navigation entry
 *
 * Hot-sale uses useHotSale (real API). Orders use useOrders (real API, 3
 * items, auth-gated). Warehouse uses useUserStatistics (real API, auth-gated).
 * No fabricated data anywhere — unauthenticated sections show navigation cards.
 */

import {
  useHotSale,
  useOrders,
  useUserStatistics,
  type OrderSummary,
} from "@kupikupi/api";
import { TRANSIT_DAYS } from "@kupikupi/brand";
import { formatPrice, type Currency, type Locale } from "@kupikupi/money";
import { Link, useNavigate } from "@tanstack/react-router";
import { useState } from "react";
import { Trans, useTranslation } from "react-i18next";
import { CameraIcon } from "../components/CameraIcon";
import { TgBottomNav } from "../components/TgBottomNav";
import { TgHeader } from "../components/TgHeader";
import { TgPage } from "../components/TgPage";
import { TgSkeleton } from "../components/TgSkeleton";
import { useAuth } from "../lib/auth";
import { useImageSearchPicker } from "../lib/image-search";
import { miniDisplayCurrency, miniDisplayLocale } from "../lib/locale-currency";

// ─── Design token tile backgrounds ────────────────────────────────────────────
const TILE_BG = [
  "linear-gradient(135deg,#FFE5DD,#FFD3C2)",
  "linear-gradient(135deg,#E0F7F1,#B8E8DD)",
  "linear-gradient(135deg,#FFE0E9,#FFC8D6)",
  "linear-gradient(135deg,#FFF8DA,#FFE5A0)",
] as const;

// ─── Order status style map (mirrors orders.tsx) ───────────────────────────────
const STATUS_STYLE: Record<string, { cls: string; emoji: string }> = {
  WAIT_PAYMENT: { cls: "warning", emoji: "⏱" },
  INIT: { cls: "warning", emoji: "⏱" },
  PURCHASING: { cls: "photo", emoji: "🛒" },
  PURCHASED: { cls: "photo", emoji: "🛒" },
  WAIT_CONFIRM: { cls: "photo", emoji: "📷" },
  SHIPPED: { cls: "transit", emoji: "✈" },
  COMPLETED: { cls: "delivered", emoji: "✓" },
  CANCELLED: { cls: "delivered", emoji: "✖" },
};

// ─── Compact order row ─────────────────────────────────────────────────────────
function OrderRow({
  order,
  displayLocale,
  displayCurrency,
  statusLabel,
}: {
  order: OrderSummary;
  displayLocale: Locale;
  displayCurrency: Currency;
  statusLabel: string;
}) {
  const { t: tOrders } = useTranslation("orders");
  const style = STATUS_STYLE[order.status] ?? { cls: "delivered", emoji: "📦" };
  const isWaitPayment = order.status === "WAIT_PAYMENT" || order.status === "INIT";
  const first = order.items[0];

  return (
    <Link
      to={"/order-detail/$orderNo" as never}
      params={{ orderNo: order.orderNo } as never}
      className="tg-order"
      style={{ textDecoration: "none", color: "inherit" }}
    >
      <div className="tg-order__top">
        <span className="tg-order__id">№ {order.orderNo}</span>
        <span
          className={style.cls === "warning" ? "tg-st" : `tg-st tg-st--${style.cls}`}
          style={
            style.cls === "warning"
              ? {
                  background: "var(--color-warning-soft)",
                  color: "var(--color-warning-foreground)",
                  border: "1px solid rgba(255,162,0,.3)",
                }
              : undefined
          }
        >
          <span
            className="tg-st__dot"
            style={style.cls === "warning" ? { background: "var(--color-warning)" } : undefined}
          />
          {statusLabel}
        </span>
      </div>
      <div className="tg-order__body">
        <div className="tg-order__img" style={{ background: "var(--color-surface)" }}>
          {first?.imageUrl ? (
            <img
              src={first.imageUrl}
              alt={first.title}
              style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: 8 }}
            />
          ) : (
            "📦"
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div className="tg-order__t">
            {first?.title ?? "—"}
            {order.items.length > 1 && ` +${order.items.length - 1}`}
          </div>
          <div className="tg-order__s">{tOrders("card.items_n", { n: order.totalQuantity })}</div>
        </div>
        <div
          className="tg-order__price"
          style={isWaitPayment ? { color: "var(--color-cta)" } : undefined}
        >
          {formatPrice(order.orderAmountRubKopecks, displayLocale, displayCurrency)}
        </div>
      </div>
    </Link>
  );
}

// ─── Main page ─────────────────────────────────────────────────────────────────
export function AppPreviewPage() {
  const { t, i18n } = useTranslation("home");
  const { t: tCommon } = useTranslation("common");
  const { t: tOrders } = useTranslation("orders");
  const { t: tWh } = useTranslation("warehouse");
  const { token: authToken } = useAuth();
  const navigate = useNavigate();
  const [draftKw, setDraftKw] = useState("");
  const imgSearch = useImageSearchPicker();

  const displayLocale = miniDisplayLocale(i18n.language);
  const displayCurrency = miniDisplayCurrency(i18n.language);

  // Screen 1: Hot-sale 2×2 grid (always fetch, no auth required)
  const hot = useHotSale({ channel: "taobao", pageNo: 1, pageSize: 4 });

  // Screen 2: Recent orders (first 3, auth-gated)
  const orders = useOrders(authToken, { pageNo: 1, pageSize: 3 });

  // Screen 3: Warehouse stats (auth-gated)
  const stats = useUserStatistics(authToken);

  const hotRecords = hot.data?.records ?? [];
  const orderList = (orders.data?.records ?? []).slice(0, 3);

  return (
    <TgPage>
      {/* Header with no back button — this is a top-level tab */}
      <TgHeader title="kupikupi" right="🔔" borderless />

      {/* ─────────────── Screen 1 · Home ──────────────── */}
      <div className="tg-hero">
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "4px 10px",
            background: "rgba(255,255,255,.7)",
            borderRadius: 999,
            fontSize: 10,
            fontWeight: 700,
            color: "var(--color-primary-fill)",
            marginBottom: 8,
          }}
        >
          {t("hero.eyebrow", { days: TRANSIT_DAYS })}
        </span>
        <h1>
          {t("hero.title_line1")}
          <br />
          {t("hero.title_line2").includes("<accent>") ? (
            <>
              {t("hero.title_line2").split("<accent>")[0]}
              <span className="accent">
                {t("hero.title_line2").split("<accent>")[1]?.split("</accent>")[0]}
              </span>
              {t("hero.title_line2").split("</accent>")[1] ?? ""}
            </>
          ) : (
            t("hero.title_line2")
          )}
        </h1>
        <p>{t("hero.subtitle")}</p>

        {/* Search bar — 1:1 design tg-search */}
        <form
          className="tg-search"
          onSubmit={(e) => {
            e.preventDefault();
            const kw = draftKw.trim();
            if (!kw && !imgSearch.isUploading) return;
            navigate({
              to: "/catalog" as never,
              search: (kw ? { keyword: kw } : {}) as never,
            });
          }}
          style={{ display: "flex", alignItems: "center", gap: 6 }}
        >
          <span style={{ color: "var(--color-text-muted)", flexShrink: 0 }}>🔍</span>
          <input
            value={draftKw}
            onChange={(e) => setDraftKw(e.target.value)}
            placeholder={t("hero.search_placeholder")}
            style={{ flex: 1 }}
          />
          <button
            type="button"
            onClick={imgSearch.open}
            disabled={imgSearch.isUploading}
            title={t("hero.search_cam_title")}
            style={{
              flexShrink: 0,
              background: "none",
              border: "none",
              cursor: "pointer",
              color: imgSearch.isUploading
                ? "var(--color-text-muted)"
                : "var(--color-primary-fill)",
              padding: "2px 6px",
              display: "flex",
              alignItems: "center",
              opacity: imgSearch.isUploading ? 0.5 : 1,
            }}
          >
            <CameraIcon size={18} />
          </button>
          <button type="submit" style={{ flexShrink: 0 }}>
            {t("hero.search_button")}
          </button>
        </form>
      </div>

      <div className="tg-body" style={{ paddingTop: 0, flex: 1 }}>

        {/* ── Invite announce strip ── */}
        <Link
          to={"/invite" as never}
          style={{
            display: "flex",
            gap: 8,
            alignItems: "center",
            background: "var(--color-sticker-yellow-soft)",
            border: "1px solid var(--color-sticker-yellow)",
            borderRadius: 10,
            padding: "8px 12px",
            fontSize: 11,
            color: "var(--color-sticker-yellow-fg)",
            textDecoration: "none",
          }}
        >
          <span>🎁</span>
          <span>
            <Trans i18nKey="invite_strip" ns="home" components={{ strong: <strong /> }} />
          </span>
        </Link>

        {/* ── Promo bear card ── */}
        <Link
          to={"/invite" as never}
          className="tg-promo"
          style={{ marginTop: "var(--space-stack-md)" }}
        >
          <div style={{ flex: 1, position: "relative", zIndex: 1 }}>
            <h3 className="tg-promo__t">
              {t("promo.title")}
              <br />
              {t("promo.subtitle")}
            </h3>
            <p className="tg-promo__s">{t("promo.desc")}</p>
            <span className="tg-promo__cta">{t("promo.cta")}</span>
          </div>
          <div className="tg-promo__bear">🐻</div>
        </Link>

        {/* ── 6-step process widget ── */}
        <div className="tg-sec-h">
          <span className="tg-sec-h__t">{t("steps.title")}</span>
          <Link
            to={"/beginner-guide" as never}
            className="tg-sec-h__r"
            style={{ textDecoration: "none" }}
          >
            {t("steps.video_cta")}
          </Link>
        </div>
        <div
          style={{
            background: "#fff",
            borderRadius: 12,
            padding: "12px 14px",
            border: "1px solid var(--color-border)",
          }}
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 4,
              alignItems: "center",
            }}
          >
            {(["🔗", "💳", "🛍", "📷", "✈", "🏠"] as const).map((ic, i) => (
              <div key={i} style={{ textAlign: "center", fontSize: 18 }}>
                {ic}
              </div>
            ))}
          </div>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(6,1fr)",
              gap: 4,
              fontSize: 9,
              color: "var(--color-text-muted)",
              textAlign: "center",
              marginTop: 4,
              fontWeight: 700,
            }}
          >
            {t("steps.labels")
              .split(",")
              .map((l: string, i: number) => (
                <span key={i}>{l}</span>
              ))}
          </div>
        </div>

        {/* ── 🔥 Hot sale 2×2 grid ── */}
        <div className="tg-sec-h">
          <span className="tg-sec-h__t">{t("hot.title")}</span>
          <Link
            to={"/catalog" as never}
            className="tg-sec-h__r"
            style={{ textDecoration: "none" }}
          >
            {t("hot.more")}
          </Link>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 8 }}>
          {hot.isLoading
            ? Array.from({ length: 4 }).map((_, i) => (
                <div
                  key={i}
                  style={{
                    background: "#fff",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: 6,
                    height: 180,
                  }}
                >
                  <div style={{ marginBottom: 6 }}>
                    <TgSkeleton height={120} radius={8} />
                  </div>
                  <div style={{ marginBottom: 4 }}>
                    <TgSkeleton height={11} width="80%" />
                  </div>
                  <TgSkeleton height={13} width="50%" />
                </div>
              ))
            : hotRecords.map((p, i) => (
                <Link
                  key={`${p.channel}-${p.spuNo}`}
                  to={"/product/$channel/$spuNo" as never}
                  params={{ channel: p.channel, spuNo: p.spuNo } as never}
                  style={{
                    background: "#fff",
                    border: "1px solid var(--color-border)",
                    borderRadius: 10,
                    padding: 6,
                    display: "flex",
                    flexDirection: "column",
                    gap: 6,
                    textDecoration: "none",
                    color: "inherit",
                  }}
                >
                  <div
                    style={{
                      aspectRatio: "1",
                      background: TILE_BG[i % TILE_BG.length],
                      borderRadius: 8,
                      display: "grid",
                      placeItems: "center",
                      fontSize: 36,
                      overflow: "hidden",
                    }}
                  >
                    {p.imageUrl ? (
                      <img
                        src={p.imageUrl}
                        alt={p.title}
                        loading="lazy"
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                      />
                    ) : (
                      "🛍️"
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      lineHeight: 1.3,
                      display: "-webkit-box",
                      WebkitLineClamp: 2,
                      WebkitBoxOrient: "vertical",
                      overflow: "hidden",
                    }}
                  >
                    {p.title}
                  </div>
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 13,
                      fontWeight: 900,
                      color: "var(--color-price)",
                    }}
                  >
                    {formatPrice(p.priceRubKopecks, displayLocale, displayCurrency)}
                  </div>
                </Link>
              ))}
          {/* Empty state when API returns nothing */}
          {!hot.isLoading &&
            hotRecords.length === 0 &&
            Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  border: "1px solid var(--color-border)",
                  borderRadius: 10,
                  padding: 6,
                  height: 180,
                  opacity: 0.4,
                }}
              />
            ))}
        </div>

        {/* ─────────────── Screen 2 · Orders preview ──────────────── */}
        <div className="tg-sec-h" style={{ marginTop: 8 }}>
          <span className="tg-sec-h__t">{tOrders("head.title")}</span>
          <Link
            to={"/orders" as never}
            className="tg-sec-h__r"
            style={{ textDecoration: "none" }}
          >
            {tCommon("cta.view_more")} →
          </Link>
        </div>

        {!authToken ? (
          /* Not signed in — navigation card only, no fake orders */
          <Link
            to={"/orders" as never}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 16px",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>📦</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {tOrders("head.title")}
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tOrders("state.signin_t")}
                </div>
              </div>
            </div>
            <span style={{ color: "var(--color-text-muted)" }}>›</span>
          </Link>
        ) : orders.isLoading ? (
          /* Loading skeleton */
          <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
            {Array.from({ length: 2 }).map((_, i) => (
              <div
                key={i}
                style={{
                  background: "#fff",
                  borderBottom: "1px solid var(--color-border)",
                  padding: "10px 12px",
                  display: "flex",
                  flexDirection: "column",
                  gap: 8,
                }}
              >
                <div style={{ display: "flex", justifyContent: "space-between" }}>
                  <TgSkeleton width={100} height={11} />
                  <TgSkeleton width={70} height={18} radius={8} />
                </div>
                <div style={{ display: "flex", gap: 10, alignItems: "center" }}>
                  <TgSkeleton width={48} height={48} radius={8} />
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 5 }}>
                    <TgSkeleton height={13} width="80%" />
                    <TgSkeleton height={11} width="50%" />
                  </div>
                  <TgSkeleton width={60} height={16} radius={4} />
                </div>
              </div>
            ))}
          </div>
        ) : orderList.length === 0 ? (
          /* Empty — prompt to create first order */
          <Link
            to={"/catalog" as never}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 16px",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>📦</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {tOrders("state.empty_t")}
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tOrders("state.empty_s")}
                </div>
              </div>
            </div>
            <span style={{ color: "var(--color-text-muted)" }}>›</span>
          </Link>
        ) : (
          /* Real orders (max 3) */
          <div
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              overflow: "hidden",
            }}
          >
            {orderList.map((order) => {
              const style = STATUS_STYLE[order.status] ?? { cls: "delivered", emoji: "📦" };
              const rawLabel = tOrders(`status.${order.status}`, {
                defaultValue: order.statusName ?? order.status,
              });
              return (
                <OrderRow
                  key={order.orderNo}
                  order={order}
                  displayLocale={displayLocale}
                  displayCurrency={displayCurrency}
                  statusLabel={`${style.emoji} ${rawLabel}`}
                />
              );
            })}
          </div>
        )}

        {/* Create new order CTA (shown only when authenticated) */}
        {authToken && (
          <Link
            to={"/catalog" as never}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "var(--color-surface)",
              border: "1px solid var(--color-border)",
              borderRadius: 10,
              padding: "10px 14px",
              fontSize: 13,
              fontWeight: 700,
              textDecoration: "none",
              color: "var(--color-primary-fill)",
              marginTop: 4,
            }}
          >
            + {tCommon("cta.create_order")}
          </Link>
        )}

        {/* ─────────────── Screen 3 · Warehouse preview ──────────────── */}
        <div className="tg-sec-h" style={{ marginTop: 8 }}>
          <span className="tg-sec-h__t">{tWh("head.title")}</span>
          <Link
            to={"/warehouse" as never}
            className="tg-sec-h__r"
            style={{ textDecoration: "none" }}
          >
            {tCommon("cta.view_more")} →
          </Link>
        </div>

        {!authToken ? (
          /* Not signed in — navigation entry card */
          <Link
            to={"/warehouse" as never}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "14px 16px",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ fontSize: 24 }}>🏬</span>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700 }}>
                  {tWh("head.title")}
                </div>
                <div style={{ fontSize: 11, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tWh("head.subtitle")}
                </div>
              </div>
            </div>
            <span style={{ color: "var(--color-text-muted)" }}>›</span>
          </Link>
        ) : (
          /* Authenticated — real stat counters from useUserStatistics */
          <Link
            to={"/warehouse" as never}
            style={{
              background: "#fff",
              border: "1px solid var(--color-border)",
              borderRadius: 12,
              padding: "12px 14px",
              display: "block",
              textDecoration: "none",
              color: "inherit",
            }}
          >
            <div style={{ display: "grid", gridTemplateColumns: "repeat(3,1fr)", gap: 6 }}>
              {/* On-warehouse count */}
              <div
                style={{
                  background: "var(--color-primary-soft)",
                  borderRadius: 10,
                  padding: "10px 8px",
                  textAlign: "center",
                }}
              >
                {stats.isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                    <TgSkeleton width={36} height={22} radius={4} />
                  </div>
                ) : (
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 20,
                      fontWeight: 900,
                      color: "var(--color-primary-fill)",
                    }}
                  >
                    {stats.data?.warehouseQuantity ?? 0}
                  </div>
                )}
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tWh("stats.stored")}
                </div>
              </div>

              {/* Awaiting photo check */}
              <div
                style={{
                  background: "var(--color-sticker-yellow-soft)",
                  borderRadius: 10,
                  padding: "10px 8px",
                  textAlign: "center",
                }}
              >
                {stats.isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                    <TgSkeleton width={36} height={22} radius={4} />
                  </div>
                ) : (
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 20,
                      fontWeight: 900,
                      color: "var(--color-sticker-yellow-fg)",
                    }}
                  >
                    {stats.data?.waitConfirmOrderQuantity ?? 0}
                  </div>
                )}
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tWh("stats.checking")}
                </div>
              </div>

              {/* Awaiting payment */}
              <div
                style={{
                  background: "var(--color-cta-soft)",
                  borderRadius: 10,
                  padding: "10px 8px",
                  textAlign: "center",
                }}
              >
                {stats.isLoading ? (
                  <div style={{ display: "flex", justifyContent: "center", marginBottom: 2 }}>
                    <TgSkeleton width={36} height={22} radius={4} />
                  </div>
                ) : (
                  <div
                    style={{
                      fontFamily: "var(--font-display)",
                      fontSize: 20,
                      fontWeight: 900,
                      color: "var(--color-cta)",
                    }}
                  >
                    {stats.data?.waitPaymentOrderQuantity ?? 0}
                  </div>
                )}
                <div style={{ fontSize: 9, color: "var(--color-text-muted)", marginTop: 2 }}>
                  {tWh("stats.ready")}
                </div>
              </div>
            </div>

            {/* Warehouse CTA footer */}
            <div
              style={{
                marginTop: 10,
                paddingTop: 10,
                borderTop: "1px solid var(--color-border)",
                fontSize: 12,
                fontWeight: 700,
                color: "var(--color-primary-fill)",
                textAlign: "center",
              }}
            >
              {tWh("head.rehearse")} →
            </div>
          </Link>
        )}

        {/* ── Support note ── */}
        <div
          style={{
            background: "var(--color-surface)",
            borderRadius: 10,
            padding: "10px 12px",
            fontSize: 11,
            color: "var(--color-text-muted)",
            lineHeight: 1.55,
            marginTop: 4,
            marginBottom: 4,
          }}
        >
          {tCommon("mini_app.support_prefix")}{" "}
          <a
            href="https://t.me/kupikupi_support_bot"
            target="_blank"
            rel="noreferrer"
            style={{ color: "var(--color-primary-fill)", fontWeight: 700 }}
          >
            @kupikupi_support
          </a>{" "}
          {tCommon("mini_app.support_suffix")}
        </div>
      </div>

      <TgBottomNav />
    </TgPage>
  );
}
